<template>
  <div class="layout-subscription">
    <div class="layout-subscription__accent" />
    <header class="layout-subscription__topbar">
      <div class="layout-subscription__topbar-inner">
        <button
          type="button"
          class="layout-subscription__back"
          :aria-label="t('billing.layout.back')"
          @click="onBack"
        >
          <span class="layout-subscription__back-icon">
            <i class="fa-regular fa-arrow-left" />
          </span>
          <span class="layout-subscription__back-label">
            {{ t('billing.layout.back') }}
          </span>
        </button>

        <a
          class="layout-subscription__brand"
          href="#"
          @click.prevent="onBack"
        >
          <img
            class="layout-subscription__logo"
            src="@/assets/images/logo-tc-black.svg"
            alt="ShopiMind"
          >
        </a>

        <div class="layout-subscription__spacer" />
      </div>
    </header>

    <main class="layout-subscription__content">
      <router-view />
    </main>
  </div>
</template>

<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Gleap from 'gleap';
import { getGleapUserId, UserState } from '@/composables/User';
import { getShopPlatformVersion, getV4DashboardUrl } from '@/composables/shopVersion';

/**
 * Legacy billing entry points whose new behaviour is to redirect to
 * `/shop-subscription`. When the user lands on `/shop-subscription` *from* one
 * of these paths, hitting "Back" would loop straight back through the redirect.
 * In that case we send them to the platform home instead.
 */
const LEGACY_BILLING_PATHS = ['/shop/plan', '/shop/bills', '/user/profile/offer'];

function pathIsLegacyBilling(path: string | null | undefined): boolean {
  if (!path) return false;
  // Strip query string / hash before comparing.
  const clean = path.split('?')[0].split('#')[0];
  return LEGACY_BILLING_PATHS.includes(clean);
}

function referrerPath(referrer: string): string | null {
  if (!referrer) return null;
  try {
    return new URL(referrer).pathname;
  } catch {
    return null;
  }
}

/**
 * Body class that drives the global CSS rule hiding Gleap's automatic UI
 * (banner / modal / notifications / tours). The Gleap SDK appends those
 * elements directly to `document.body`, outside this component's tree, so
 * scoped CSS can't reach them — and `setDisableInAppNotifications` only
 * covers the `notification` actionType, NOT banners. Hiding via a body
 * class is timing-proof: it works even if Gleap injects the banner after
 * mount (server-side outbound rules).
 */
const GLEAP_SILENT_BODY_CLASS = 'subscription-no-gleap-ui';

export default defineComponent({
  name: 'LayoutSubscription',

  setup() {
    const { t } = useI18n();
    const router = useRouter();

    // Gleap is normally bootstrapped by LayoutMain, but this layout is
    // standalone (the subscription page lives outside the dashboard
    // sidebar). Initialise it here too so that the "Contact us" CTA on
    // the Pro plan card actually opens the chat widget. The Gleap SDK
    // is idempotent — calling initialize twice with the same key is safe.
    //
    // On this page Gleap is kept silent: only the explicit "Contact us"
    // CTA opens it via Gleap.open(). The floating feedback button and any
    // automatic in-app banners (surveys, outbound campaigns, news) are
    // suppressed so the subscription flow is not interrupted. Defaults are
    // restored on unmount to leave LayoutMain unaffected.
    onMounted(() => {
      // Hide any Gleap automatic UI for as long as this layout is mounted.
      // CSS-based, so it covers banners injected *after* mount too — which
      // the JS flags below cannot (banners bypass setDisableInAppNotifications).
      document.body.classList.add(GLEAP_SILENT_BODY_CLASS);

      try {
        Gleap.setLanguage(UserState.user?.lang ?? 'en');
        Gleap.initialize(process.env.VUE_APP_GLEAP_API_KEY || '');
        Gleap.setMaxNetworkRequests(50);
        Gleap.showFeedbackButton(false);
        Gleap.setDisableInAppNotifications(true);
        // Tear down a banner / modal that was already open before we landed
        // here (e.g. opened earlier by LayoutMain during the session).
        Gleap.closeBanner();
        Gleap.closeModal();
        if (UserState.user?.id && !Gleap.isUserIdentified()) {
          Gleap.identify(getGleapUserId(), {
            email: UserState.user.email,
            name: `${UserState.user.firstName ?? ''} ${UserState.user.lastName ?? ''}`.trim(),
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[LayoutSubscription] Gleap init failed', e);
      }
    });

    onBeforeUnmount(() => {
      // Restore Gleap defaults so the rest of the app (LayoutMain) keeps
      // its banners and floating button active.
      document.body.classList.remove(GLEAP_SILENT_BODY_CLASS);
      try {
        Gleap.showFeedbackButton(true);
        Gleap.setDisableInAppNotifications(false);
      } catch {
        // Gleap may not be loaded — fail silently.
      }
    });

    // Captured ONCE at mount: cross-domain provenance (e.g. user clicked a
    // link on my.shopimind.com that opened my.new.shopimind.com/shop-subscription).
    // `document.referrer` is set at page load and never mutates afterwards.
    const initialReferrer = typeof document !== 'undefined' ? document.referrer : '';

    /**
     * Sends the user to the home of the *platform* the shop currently runs on
     * (v4 → external my.shopimind.com, v5 → in-app /dashboard). Used when the
     * normal "back" would loop through a redirected legacy billing path.
     */
    const goToPlatformHome = () => {
      const platform = getShopPlatformVersion();
      if (platform === 'v4') {
        window.location.href = getV4DashboardUrl();
        return;
      }
      router.push({ name: 'home' });
    };

    const onBack = () => {
      // 1. Intra-app provenance (Vue Router pushed us here from another route
      //    of the new app). `history.state.back` is the path of the previous
      //    entry in the browser history stack.
      const intraAppBack = (window.history.state as any)?.back as string | null | undefined;
      if (pathIsLegacyBilling(intraAppBack)) {
        goToPlatformHome();
        return;
      }

      // 2. Cross-domain provenance (user came from the v4 frontend on
      //    my.shopimind.com). Look at the page's referrer captured at mount.
      if (pathIsLegacyBilling(referrerPath(initialReferrer))) {
        goToPlatformHome();
        return;
      }

      // 3. Default: standard back, with a home fallback when the history is
      //    empty (deep link / fresh tab).
      if (window.history.length > 1) {
        router.back();
        return;
      }
      goToPlatformHome();
    };

    return { t, onBack };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.layout-subscription {
  min-height: 100vh;
  background: #f8fafc;
  display: flex;
  flex-direction: column;

  // Thin brand accent at the very top of the page — anchors identity
  &__accent {
    height: 3px;
    background: linear-gradient(
      90deg,
      $brand-color-primary 0%,
      #b6dc6a 50%,
      $brand-color-primary 100%
    );
    flex-shrink: 0;
  }

  &__topbar {
    background: #fff;
    box-shadow:
      0 1px 0 rgba(15, 23, 42, 0.06),
      0 4px 16px -8px rgba(15, 23, 42, 0.08);
    height: 72px;
    display: flex;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  &__topbar-inner {
    width: 100%;
    max-width: 1180px;
    margin: 0 auto;
    padding: 0 28px;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 16px;
  }

  // ─── Back button — pill with leading icon circle ──────────────────
  &__back {
    justify-self: start;
    appearance: none;
    background: #fff;
    border: 1px solid #e2e8f0;
    padding: 6px 18px 6px 6px;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 0.875rem;
    color: #334155;
    cursor: pointer;
    border-radius: 100px;
    transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 600;
    line-height: 1;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

    &:hover {
      border-color: $brand-color-primary;
      color: #0f172a;
      transform: translateX(-2px);
      box-shadow: 0 4px 12px -4px rgba(148, 200, 64, 0.35);

      .layout-subscription__back-icon {
        background: $brand-color-primary;
        color: #fff;
      }
    }

    &:active {
      transform: translateX(-2px) scale(0.98);
    }
  }

  &__back-icon {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #f1f5f9;
    color: #475569;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 200ms cubic-bezier(0.4, 0, 0.2, 1);

    i {
      font-size: 0.75rem;
    }
  }

  &__back-label {
    letter-spacing: -0.01em;
  }

  // ─── Brand (logo centered) ────────────────────────────────────────
  &__brand {
    justify-self: center;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    transition: opacity 160ms ease;

    &:hover {
      opacity: 0.75;
    }
  }

  &__logo {
    height: 32px;
    width: auto;
    display: block;
  }

  &__spacer {
    justify-self: end;
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 32px 28px 48px;
  }

  @media (max-width: 640px) {
    &__topbar {
      height: 60px;
    }

    &__topbar-inner {
      padding: 0 16px;
    }

    &__back {
      padding: 4px 4px;
    }

    &__back-label {
      display: none;
    }

    &__logo {
      height: 28px;
    }

    &__content {
      padding: 20px 16px 32px;
    }
  }
}
</style>

<!--
  Global (non-scoped) on purpose: Gleap appends its automatic UI directly to
  document.body, outside this component's DOM tree. While the subscription
  layout is mounted, every automatic Gleap surface is hidden — only the
  conversation widget opened explicitly via Gleap.open() (the "Nous contacter"
  CTA) stays available. The body class is added on mount / removed on unmount,
  so the rest of the app (LayoutMain) is unaffected.
-->
<style lang="scss">
body.subscription-no-gleap-ui {
  .gleap-b,                       /* outbound banner */
  .gleap-modal,                   /* outbound modal */
  .gleap-modal-wrapper,
  .gleap-modal-backdrop,
  .gleap-notification-container,  /* in-app notification stack */
  .gleap-tooltip,                 /* product-tour tooltips / hotspots */
  .gleap-tour-message {           /* product tours */
    display: none !important;
  }
}
</style>
